import { useTranslation } from 'react-i18next';
import { MainLayout } from '../../Layout/MainLayout';
import {
  Box,
  BrightyStepButton,
  Button,
  Grid,
  HookForm,
  Stack,
  Step,
  Stepper,
  useDebounce,
} from '@razz-team/ui-web';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Route } from '../../../routes/account.transfer';
import { TransferAmountStep } from './TransferAmountStep';
import {
  TransferFormValues,
  mapTransferBeneficiaryToApi,
  mapTransferBeneficiaryToCreateApi,
  transferValuesFromTransaction,
  trimOrNull,
} from './types';
import { TransferBeneficiaryStep } from './TransferBeneficiaryStep';
import { useAccountsWithAccountQuery } from '../../../api/accounts';
import {
  useCreateInternalTransferMutation,
  useCreateOutgoingExternalTransferMutation,
  useInternalTransferIntentQuery,
  useOutgoingExternalTransferIntentQuery,
} from '../../../api/transfers';
import {
  ApiExternalOutgoingTransferCreateResponse,
  ApiExternalOutgoingTransferIntentRequest,
  ApiInternalTransferIntentRequest,
} from '../../../openapi';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import {
  mapClientMoneyToServerMoney,
  parseFloatValue,
  serverAmountToClientAmount,
} from '@razz-team/utils';
import { TransferConfirmStep } from './TransferConfirmStep';
import {
  calculateDefaultAccountId,
  isBalanceSufficientForExternal,
  isBalanceSufficientForInternal,
} from './calculateDefaultAccountId';
import { useFormError } from '../../../hooks/useFormError';
import { useTransactionQuery } from '../../../api/transactions';

enum TransferSteps {
  beneficiary,
  amount,
  confirm,
}

export function TransferPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState<TransferSteps>(TransferSteps.beneficiary);
  const search = Route.useSearch();
  const form = useForm<TransferFormValues>({
    defaultValues: {
      beneficiary: {},
    },
  });

  const transactionQuery = useTransactionQuery({
    transactionId: search.transactionId,
    createdAt: search.createdAt,
  });
  useEffect(() => {
    if (transactionQuery.data) {
      const values = transferValuesFromTransaction(transactionQuery.data);
      if (values) {
        form.reset(values);
        setStep(TransferSteps.amount);
      }
    }
  }, [form, transactionQuery.data]);

  const currency = form.watch('beneficiary.value.currency');
  const beneficiary = form.watch('beneficiary');

  const accountsQuery = useAccountsWithAccountQuery(search.accountId);
  const accounts = useMemo(() => {
    const all = accountsQuery.data ?? [];
    return beneficiary.type === 'internal' && beneficiary.value?.currency
      ? all.filter((it) => it.balance.currency === beneficiary.value.currency)
      : all;
  }, [accountsQuery.data, beneficiary?.type, beneficiary?.value?.currency]);
  const accountId = form.watch('accountId');

  const updateDefaultAccountId = useCallback(() => {
    const values = form.getValues();
    form.setValue(
      'accountId',
      calculateDefaultAccountId({
        accounts,
        currency: values.beneficiary.value.currency,
        defaultAccountId: search.accountId,
      }),
    );
  }, [accounts, form, search.accountId]);

  const goToBeneficiary = useCallback(() => setStep(TransferSteps.beneficiary), []);
  const goToAmount = useCallback(() => {
    updateDefaultAccountId();
    setStep(TransferSteps.amount);
  }, [updateDefaultAccountId]);

  const [externalIntentRequest, setExternalIntentRequest] = useState<
    ApiExternalOutgoingTransferIntentRequest | undefined
  >();
  const [internalIntentRequest, setInternalIntentRequest] = useState<
    ApiInternalTransferIntentRequest | undefined
  >();

  const handleMaxClick = useCallback(() => {
    const account = accounts.find(({ id }) => id === accountId);
    if (account) {
      if (beneficiary.type === 'internal') {
        setInternalIntentRequest({
          side: 'SELL',
          amount: account.balance,
          sourceCurrency: account.balance.currency,
          targetCurrency: currency,
        });
      } else {
        const mappedBeneficiary = mapTransferBeneficiaryToApi(beneficiary);
        if (mappedBeneficiary) {
          setExternalIntentRequest({
            side: 'SELL',
            amount: account.balance,
            beneficiary: mappedBeneficiary,
            sourceCurrency: account.balance.currency,
            targetCurrency: currency,
          });
        }
      }
    }
  }, [accountId, accounts, beneficiary, currency]);

  const clientAmount = form.watch('clientAmount');
  const ignoreIntentUpdateRef = useRef<boolean>(false);
  useEffect(() => {
    if (ignoreIntentUpdateRef.current) {
      ignoreIntentUpdateRef.current = false;
      return;
    }

    const parsedAmount = parseFloatValue(clientAmount);
    if (parsedAmount > 0) {
      const account = accounts.find(({ id }) => id === accountId);
      if (account) {
        const amount = mapClientMoneyToServerMoney({
          amount: parsedAmount,
          currency,
        });
        if (beneficiary.type === 'internal') {
          setInternalIntentRequest({
            amount,
            side: 'BUY',
            sourceCurrency: account.balance.currency,
            targetCurrency: currency,
          });
        } else {
          const mappedBeneficiary = mapTransferBeneficiaryToApi(beneficiary);
          if (mappedBeneficiary) {
            setExternalIntentRequest({
              amount,
              side: 'BUY',
              sourceCurrency: account.balance.currency,
              targetCurrency: currency,
              beneficiary: mappedBeneficiary,
            });
          }
        }
      }
    }
  }, [accountId, accounts, beneficiary, clientAmount, currency]);

  const debouncedExternalIntentRequest = useDebounce(externalIntentRequest, 500);
  const externalIntentQuery = useOutgoingExternalTransferIntentQuery(
    debouncedExternalIntentRequest,
  );
  const debouncedInternalIntentRequest = useDebounce(internalIntentRequest, 500);
  const internalIntentQuery = useInternalTransferIntentQuery(debouncedInternalIntentRequest);

  const intentSide = externalIntentRequest?.side ?? internalIntentRequest?.side;
  const intentQuote = externalIntentQuery.data?.quote ?? internalIntentQuery.data?.quote;
  useEffect(() => {
    if (intentQuote && intentSide === 'SELL') {
      ignoreIntentUpdateRef.current = true;
      form.setValue(
        'clientAmount',
        serverAmountToClientAmount(intentQuote.targetAmount).toString(),
      );
    }
  }, [form, intentSide, intentQuote]);

  const idempotencyKey = useIdempotencyKey();
  const createOutgoingTransfer = useCreateOutgoingExternalTransferMutation({ idempotencyKey });
  const createInternalTransfer = useCreateInternalTransferMutation({ idempotencyKey });

  useFormError(
    form,
    'solution',
    step === TransferSteps.confirm
      ? (createOutgoingTransfer.error ?? createInternalTransfer.error)
      : undefined,
  );
  useFormError(
    form,
    'clientAmount',
    step === TransferSteps.amount
      ? (externalIntentQuery.error ?? createOutgoingTransfer.error ?? createInternalTransfer.error)
      : undefined,
  );

  const handleSubmit = useCallback(
    (values: TransferFormValues) => {
      switch (step) {
        case TransferSteps.beneficiary:
          updateDefaultAccountId();
          setStep(TransferSteps.amount);
          break;
        case TransferSteps.amount:
          if (values.beneficiary.type === 'internal') {
            if (!internalIntentQuery.data || internalIntentQuery.isFetching) return;

            const amount = mapClientMoneyToServerMoney({
              amount: parseFloatValue(values.clientAmount),
              currency: values.beneficiary.value.currency,
            });
            if (
              !isBalanceSufficientForInternal({
                amount,
                accounts,
                accountId: values.accountId,
              })
            ) {
              form.setError('clientAmount', {
                type: 'manual',
                message: t('Amount exceeds account balance'),
              });
              return;
            }

            createInternalTransfer.mutate({
              hash: internalIntentQuery.data.hash,
              quote: internalIntentQuery.data.quote,
              comment: values.comment,
              receiverId: values.beneficiary.value.receiverId,
              sourceAccountId: values.accountId,
            });
          } else {
            if (!externalIntentQuery.data || externalIntentQuery.isFetching) return;

            if (
              !isBalanceSufficientForExternal({
                intent: externalIntentQuery.data,
                accounts,
                accountId: values.accountId,
              })
            ) {
              form.setError('clientAmount', {
                type: 'manual',
                message: t('Amount exceeds account balance'),
              });
              return;
            }

            createOutgoingTransfer.mutate({
              fees: externalIntentQuery.data.fees,
              hash: externalIntentQuery.data.hash,
              quote: externalIntentQuery.data.quote,
              evidence: values.evidence,
              reference: trimOrNull(values.reference),
              sourceAccountId: values.accountId,
              ...mapTransferBeneficiaryToCreateApi(values.beneficiary),
            });
          }

          break;
        case TransferSteps.confirm:
          if (createOutgoingTransfer.variables && values.solution) {
            createOutgoingTransfer.mutate({
              ...createOutgoingTransfer.variables,
              solution: values.solution,
            });
          } else if (createInternalTransfer.variables && values.solution) {
            createInternalTransfer.mutate({
              ...createInternalTransfer.variables,
              solution: values.solution,
            });
          }
          break;
      }
    },
    [
      step,
      updateDefaultAccountId,
      externalIntentQuery.data,
      externalIntentQuery.isFetching,
      internalIntentQuery.data,
      internalIntentQuery.isFetching,
      accounts,
      createOutgoingTransfer,
      createInternalTransfer,
      form,
      t,
    ],
  );

  const [challenge, setChallenge] = useState<
    ApiExternalOutgoingTransferCreateResponse | undefined
  >();
  useEffect(() => {
    if (createOutgoingTransfer.data) {
      setChallenge(createOutgoingTransfer.data);
      setStep(TransferSteps.confirm);
    }
  }, [createOutgoingTransfer.data]);
  useEffect(() => {
    if (createInternalTransfer.data) {
      setChallenge(createInternalTransfer.data);
      setStep(TransferSteps.confirm);
    }
  }, [createInternalTransfer.data]);

  let isSubmitVisible = true;
  if (step === TransferSteps.beneficiary && !beneficiary?.type) {
    isSubmitVisible = false;
  } else if (createOutgoingTransfer.data?.type === 'CHALLENGES_COMPLETED') {
    isSubmitVisible = false;
  } else if (createInternalTransfer.data?.type === 'CHALLENGES_COMPLETED') {
    isSubmitVisible = false;
  }

  if (transactionQuery.isLoading) return null;

  return (
    <MainLayout title={t('Transfer')} compact>
      <Grid size={12}>
        <Box sx={{ width: '100%' }}>
          <Stepper alternativeLabel activeStep={step} nonLinear>
            <Step
              active={step === TransferSteps.beneficiary}
              disabled={step === TransferSteps.confirm}
            >
              <BrightyStepButton onClick={goToBeneficiary}>{t('Recipient')}</BrightyStepButton>
            </Step>
            <Step active={step === TransferSteps.amount}>
              <BrightyStepButton>{t('Amount')}</BrightyStepButton>
            </Step>
            <Step active={step === TransferSteps.confirm}>
              <BrightyStepButton>{t('Confirmation')}</BrightyStepButton>
            </Step>
          </Stepper>
        </Box>
      </Grid>
      <Grid
        container
        display="flex"
        justifyContent="center"
        component={HookForm}
        form={form}
        onSubmit={handleSubmit}
        size={12}
      >
        <Stack direction="column" width="100%" maxWidth={500} spacing={6} alignItems="stretch">
          {step === TransferSteps.beneficiary ? (
            <TransferBeneficiaryStep form={form} onFinish={goToAmount} internalEnabled />
          ) : null}
          {step === TransferSteps.amount ? (
            <TransferAmountStep
              form={form}
              accounts={accounts}
              intent={externalIntentQuery.data ?? internalIntentQuery.data ?? null}
              onMaxClick={handleMaxClick}
            />
          ) : null}
          {step === TransferSteps.confirm ? (
            <TransferConfirmStep
              form={form}
              accounts={accounts}
              intent={externalIntentQuery.data ?? internalIntentQuery.data}
              challenge={challenge}
            />
          ) : null}
          {isSubmitVisible ? (
            <Button
              type="submit"
              size="large"
              color="primary"
              loading={
                externalIntentQuery.isLoading ||
                externalIntentQuery.isError ||
                internalIntentQuery.isLoading ||
                internalIntentQuery.isError ||
                createOutgoingTransfer.isPending ||
                createInternalTransfer.isPending
              }
              fullWidth
            >
              {step === TransferSteps.confirm ? t('Confirm') : t('Continue')}
            </Button>
          ) : null}
        </Stack>
      </Grid>
    </MainLayout>
  );
}
